/**
 * GameSimulationService.ts
 * Handles daily game simulation from database schedule
 * 
 * Simulation engine is inline to avoid Vite bundling issues.
 */

import { supabase, isSupabaseConfigured } from './supabase';
import { simulateGame as simulateGameV2, SimPlayer as V2SimPlayer, GameResult, TeamStrategy as V2TeamStrategy } from '../engine/SimEngineV2';

export interface ScheduledGameFromDB {
  id: string;
  league_id: string;
  game_id: string;
  series_id: string;
  home_team_id: string;
  away_team_id: string;
  game_day: number;
  game_number: number;
  status: string;
  home_score?: number;
  away_score?: number;
  winner?: string;
}

export interface TeamFromDB {
  id: string;
  name: string;
  city: string;
  tier: number;
  conference?: string;
  division?: string;
  strategy?: TeamStrategy;
}

export interface TeamStrategy {
  baseRunningAggression: number;
  hittingApproach: number;
  pitcherManagement: number;
  shiftFrequency: number;
  stealAttempts: number;
  sacrificeBunts: number;
  intentionalWalks: number;
}

const DEFAULT_STRATEGY: TeamStrategy = {
  baseRunningAggression: 50,
  hittingApproach: 50,
  pitcherManagement: 50,
  shiftFrequency: 50,
  stealAttempts: 50,
  sacrificeBunts: 50,
  intentionalWalks: 50,
};

// Get games scheduled for a specific day
export async function getGamesForDay(day: number): Promise<ScheduledGameFromDB[]> {
  if (!isSupabaseConfigured() || !supabase) return [];
  
  const { data } = await supabase
    .from('league_schedule')
    .select('*')
    .eq('game_day', day)
    .eq('status', 'scheduled');
  
  return data || [];
}

// Get all games for a league on a specific day
export async function getLeagueGamesForDay(leagueId: string, day: number): Promise<ScheduledGameFromDB[]> {
  if (!isSupabaseConfigured() || !supabase) return [];
  
  const { data } = await supabase
    .from('league_schedule')
    .select('*')
    .eq('league_id', leagueId)
    .eq('game_day', day)
    .eq('status', 'scheduled');
  
  return data || [];
}

// Get team info for simulation
export async function getTeamById(teamId: string): Promise<TeamFromDB | null> {
  if (!isSupabaseConfigured() || !supabase) return null;
  
  const { data } = await supabase
    .from('teams')
    .select('id, name, city, tier, conference, division, rotation_order')
    .eq('id', teamId)
    .single();
  
  return data;
}

// Get players for a team (for simulation)
export async function getTeamPlayers(teamId: string): Promise<any[]> {
  if (!isSupabaseConfigured() || !supabase) return [];
  
  // Include players where retired is false OR null (many players never have retired set explicitly)
  const { data } = await supabase
    .from('players')
    .select('*')
    .eq('team_id', teamId)
    .or('retired.eq.false,retired.is.null');
  
  return data || [];
}

// ============================================================
// INLINE SIMULATION ENGINE
// (Self-contained to avoid Vite bundling issues with GameSimulator.ts)
// ============================================================

interface SimPlayer {
  id: string;
  name: string;
  position: string;
  is_hitter: boolean;
  is_starter?: boolean;
  batting_hand: string;
  throwing_hand: string;
  power: number;
  contact: number;
  speed: number;
  discipline: number;
  fielding: number;
  arm: number;
  velocity: number;
  control: number;
  movement: number;
  stamina: number;
  endurance: number;
  // Hidden attributes
  clutch: number;
  consistency: number;
  durability: number;
  intelligence: number;
}

interface SimTeam {
  id: string;
  city: string;
  name: string;
  players: SimPlayer[];
  strategy: TeamStrategy;
}

interface PitcherLine {
  player_id: string;
  name: string;
  is_starter: boolean;
  innings_pitched: number;
  batters_faced: number;
  pitches_thrown: number;
  hits_allowed: number;
  runs_allowed: number;
  earned_runs: number;
  walks_allowed: number;
  strikeouts: number;
  home_runs_allowed: number;
  win: boolean;
  loss: boolean;
  save: boolean;
}

interface BatterLine {
  player_id: string;
  name: string;
  position: string;
  plate_appearances: number;
  at_bats: number;
  runs: number;
  hits: number;
  doubles: number;
  triples: number;
  home_runs: number;
  rbi: number;
  walks: number;
  strikeouts: number;
  stolen_bases: number;
  caught_stealing: number;
  hit_by_pitch: number;
  sacrifice_flies: number;
  sacrifice_hits: number;
  grounded_into_dp: number;
}

interface FieldingLine {
  player_id: string;
  name: string;
  position: string;
  putouts: number;
  assists: number;
  errors: number;
  double_plays: number;
}

interface GameBoxScore {
  game_id: string;
  home_team_id: string;
  away_team_id: string;
  home_score: number;
  away_score: number;
  home_hits: number;
  away_hits: number;
  home_errors: number;
  away_errors: number;
  innings: number;
  line_score: { inning: number; away_runs: number; home_runs: number }[];
  home_pitchers: PitcherLine[];
  away_pitchers: PitcherLine[];
  home_batters: BatterLine[];
  away_batters: BatterLine[];
  home_fielding: FieldingLine[];
  away_fielding: FieldingLine[];
  winning_pitcher_id: string | null;
  losing_pitcher_id: string | null;
  save_pitcher_id: string | null;
}

function dbPlayerToSimPlayer(dbPlayer: any): SimPlayer {
  const stamina = dbPlayer.current_stamina ?? 100;
  return {
    id: dbPlayer.id,
    name: `${dbPlayer.first_name} ${dbPlayer.last_name}`,
    position: dbPlayer.position || 'OF',
    is_hitter: dbPlayer.is_hitter === true,
    is_starter: dbPlayer.is_hitter === false, // all pitchers default; rotation selection marks starters later
    batting_hand: dbPlayer.batting_hand || 'R',
    throwing_hand: dbPlayer.throwing_hand || 'R',
    power: dbPlayer.power ?? 50,
    contact: dbPlayer.contact ?? 50,
    speed: dbPlayer.speed ?? 50,
    discipline: dbPlayer.discipline ?? 50,
    fielding: dbPlayer.fielding ?? 50,
    arm: dbPlayer.arm ?? 50,
    velocity: dbPlayer.velocity ?? 50,
    control: dbPlayer.control ?? 50,
    movement: dbPlayer.movement ?? 50,
    stamina: stamina,
    endurance: stamina,
    // Hidden attributes
    clutch: dbPlayer.clutch ?? 50,
    consistency: dbPlayer.consistency ?? 50,
    durability: dbPlayer.durability ?? 50,
    intelligence: dbPlayer.intelligence ?? 50,
  };
}

// Build lineup: top 9 hitters (exclude pitchers)
function buildLineup(hitters: SimPlayer[]): SimPlayer[] {
  const nonPitchers = [...hitters]
    .filter(h => h.position !== 'P')
    .sort((a, b) => {
      const aR = (a.contact || 50) * 0.4 + (a.power || 50) * 0.4 + (a.speed || 50) * 0.2;
      const bR = (b.contact || 50) * 0.4 + (b.power || 50) * 0.4 + (b.speed || 50) * 0.2;
      return bR - aR;
    });

  const starters = nonPitchers.slice(0, 9);
  const bench = nonPitchers.slice(9);

  // Rest days: ~15% chance per starter sits out (catchers more likely)
  const lineup: SimPlayer[] = [];
  for (const starter of starters) {
    const restChance = starter.position === 'C' ? 0.20 : 0.12;
    if (Math.random() < restChance && bench.length > 0) {
      // Find bench replacement at same position, or best available
      const samePos = bench.filter(b => b.position === starter.position && !lineup.includes(b));
      const replacement = samePos.length > 0
        ? samePos[0]
        : bench.filter(b => !lineup.includes(b)).sort((a, b) =>
            ((b.contact || 50) + (b.power || 50)) - ((a.contact || 50) + (a.power || 50))
          )[0];
      if (replacement) {
        lineup.push(replacement);
        continue;
      }
    }
    lineup.push(starter);
  }
  return lineup;
}

// Get bench players (non-starters who can pinch hit)
function getBench(hitters: SimPlayer[], lineup: SimPlayer[]): SimPlayer[] {
  return hitters.filter(h =>
    h.position !== 'P' &&
    !lineup.some(l => l.id === h.id)
  );
}

// Pinch hit: replace weak batter in late innings when trailing
function tryPinchHit(
  lineup: SimPlayer[],
  bench: SimPlayer[],
  battingStats: Map<string, BatterLine>,
  inning: number,
  isHome: boolean,
  teamScore: number,
  oppScore: number,
  batterIdx: number
): number {
  // Only in 7th+ inning, trailing or tied, with bench available
  if (inning < 7 || bench.length === 0) return batterIdx;
  if (teamScore > oppScore + 2) return batterIdx; // Winning comfortably, no need

  const currentBatter = lineup[batterIdx % lineup.length];
  const currentRating = (currentBatter.contact || 50) + (currentBatter.power || 50);

  // Only pinch hit for weak hitters (below average)
  if (currentRating > 100) return batterIdx;

  // 40% chance to pinch hit in this situation
  if (Math.random() > 0.40) return batterIdx;

  // Find best bench bat
  const availableBench = bench.filter(b => !lineup.some(l => l.id === b.id));
  if (availableBench.length === 0) return batterIdx;

  const bestBench = availableBench.sort((a, b) =>
    ((b.contact || 50) + (b.power || 50)) - ((a.contact || 50) + (a.power || 50))
  )[0];

  // Only pinch hit if bench player is better
  const benchRating = (bestBench.contact || 50) + (bestBench.power || 50);
  if (benchRating <= currentRating) return batterIdx;

  // Make the swap
  lineup[batterIdx % lineup.length] = bestBench;
  // Initialize stats for pinch hitter
  if (!battingStats.has(bestBench.id)) {
    battingStats.set(bestBench.id, {
      player_id: bestBench.id, name: bestBench.name, position: bestBench.position,
      plate_appearances: 0, at_bats: 0, runs: 0, hits: 0, doubles: 0,
      triples: 0, home_runs: 0, rbi: 0, walks: 0, strikeouts: 0,
      stolen_bases: 0, caught_stealing: 0, hit_by_pitch: 0, sacrifice_flies: 0,
      sacrifice_hits: 0, grounded_into_dp: 0,
    });
  }
  return batterIdx;
}

// Defensive substitution: when leading late, bring in better fielder
function tryDefensiveSub(
  lineup: SimPlayer[],
  bench: SimPlayer[],
  fieldingStats: Map<string, FieldingLine>,
  battingStats: Map<string, BatterLine>,
  inning: number,
  teamScore: number,
  oppScore: number
): void {
  // Only in 8th+ inning, leading by 1-3 runs
  if (inning < 8) return;
  const lead = teamScore - oppScore;
  if (lead < 1 || lead > 4) return;
  if (bench.length === 0) return;

  // Find worst fielder in lineup
  const worstFielderIdx = lineup.reduce((worstIdx, player, idx) => {
    if (player.position === 'DH') return worstIdx;
    const worstRating = lineup[worstIdx]?.fielding || 50;
    return (player.fielding || 50) < worstRating ? idx : worstIdx;
  }, 0);

  const worstFielder = lineup[worstFielderIdx];
  if (!worstFielder || worstFielder.position === 'DH') return;
  if ((worstFielder.fielding || 50) > 60) return; // Already decent

  // Find bench player with better fielding at same position
  const betterFielder = bench
    .filter(b => b.position === worstFielder.position && (b.fielding || 50) > (worstFielder.fielding || 50) + 5)
    .sort((a, b) => (b.fielding || 50) - (a.fielding || 50))[0];

  if (betterFielder && Math.random() < 0.50) {
    lineup[worstFielderIdx] = betterFielder;
    // Initialize fielding stats for sub if not present
    if (!fieldingStats.has(betterFielder.id)) {
      fieldingStats.set(betterFielder.id, mkFieldingLine(betterFielder));
    }
    // Initialize batting stats for sub if not present
    if (!battingStats.has(betterFielder.id)) {
      battingStats.set(betterFielder.id, mkBatterLine(betterFielder));
    }
  }
}

// Create pitcher state
function mkPitcherState(player: SimPlayer, isStarter: boolean) {
  const stamina = player?.stamina || 50;
  const baseEnd = isStarter ? 100 : 50;
  const endurance = baseEnd * (0.6 + (stamina / 100) * 0.8);
  return {
    player, is_starter: isStarter, batters_faced: 0, pitches_thrown: 0,
    hits_allowed: 0, runs_allowed: 0, earned_runs: 0, walks_allowed: 0,
    strikeouts: 0, home_runs_allowed: 0, outs_recorded: 0,
    consecutive_batters_faced: 0, endurance_remaining: endurance,
    win: false, loss: false, save: false,
  };
}

// Create batter line
function mkBatterLine(p: SimPlayer): BatterLine {
  return {
    player_id: p.id, name: p.name, position: p.position,
    plate_appearances: 0, at_bats: 0, runs: 0, hits: 0, doubles: 0,
    triples: 0, home_runs: 0, rbi: 0, walks: 0, strikeouts: 0,
    stolen_bases: 0, caught_stealing: 0, hit_by_pitch: 0, sacrifice_flies: 0,
    sacrifice_hits: 0, grounded_into_dp: 0,
  };
}

// Create fielding line
function mkFieldingLine(p: SimPlayer): FieldingLine {
  return { player_id: p.id, name: p.name, position: p.position, putouts: 0, assists: 0, errors: 0, double_plays: 0 };
}

// Fatigue: ~2 pitches = 1 endurance, stress costs more
function drainEndurance(pitcher: any, runnersOnBase: number) {
  let cost = 0.5;
  cost += ((pitcher.player.velocity || 50) - 50) * 0.15 / 100;
  cost *= (1.0 + runnersOnBase * 0.25);
  // Durability reduces stamina drain (high durability = slower drain)
  const durabilityMod = 1 - ((pitcher.player.durability || 50) - 50) / 250; // ±20%
  cost *= durabilityMod;
  pitcher.endurance_remaining = Math.max(0, pitcher.endurance_remaining - cost);
}

function getFatigue(pitcher: any) {
  const maxE = pitcher.is_starter ? 100 : 50;
  const stam = pitcher?.player?.stamina || 50;
  const scaled = maxE * (0.6 + (stam / 100) * 0.8);
  const pct = pitcher.endurance_remaining / scaled;
  if (pct > 0.70) return { v: 1.0, c: 1.0, m: 1.0 };
  const fatigue = 1.0 - (pct / 0.70);
  const resist = 0.7 + (stam / 100) * 0.3;
  const f = fatigue * resist;
  return { v: 1.0 - f * 0.12, c: 1.0 - f * 0.18, m: 1.0 - f * 0.08 };
}

// Simulate a full game with play-by-play
function simulateGameInline(homeTeam: SimTeam, awayTeam: SimTeam, gameId: string): GameBoxScore {
  const homeHitters = homeTeam.players.filter(p => p.is_hitter);
  const awayHitters = awayTeam.players.filter(p => p.is_hitter);
  const homeLineup = buildLineup(homeHitters);
  const awayLineup = buildLineup(awayHitters);
  const homeBench = getBench(homeHitters, homeLineup);
  const awayBench = getBench(awayHitters, awayLineup);
  const homePitchers = homeTeam.players.filter(p => !p.is_hitter);
  const awayPitchers = awayTeam.players.filter(p => !p.is_hitter);

  // Assign fielders
  const assignFielders = (hitters: SimPlayer[]) => {
    const pos = ['C','1B','2B','3B','SS','LF','CF','RF','DH'];
    const map = new Map<string, SimPlayer>();
    const used = new Set<string>();
    for (const p of pos) {
      const avail = hitters.filter(h => !used.has(h.id));
      const best = avail.sort((a, b) => (b.fielding || 50) - (a.fielding || 50))[0];
      if (best) { map.set(p, best); used.add(best.id); }
    }
    return map;
  };

  const homeFielders = assignFielders(homeHitters);
  const awayFielders = assignFielders(awayHitters);

  let homeScore = 0, awayScore = 0, homeHits = 0, awayHits = 0, homeErrors = 0, awayErrors = 0;
  // Track each run for go-ahead run determination
  const runEvents: { pitcherId: string; isHomeScoring: boolean; homeAfter: number; awayAfter: number }[] = [];
  const runners = { first: null as string | null, second: null as string | null, third: null as string | null };

  const homeBatterStats = new Map<string, BatterLine>();
  const awayBatterStats = new Map<string, BatterLine>();
  const homeFieldingStats = new Map<string, FieldingLine>();
  const awayFieldingStats = new Map<string, FieldingLine>();
  homeLineup.forEach(p => homeBatterStats.set(p.id, mkBatterLine(p)));
  awayLineup.forEach(p => awayBatterStats.set(p.id, mkBatterLine(p)));
  homeFielders.forEach(p => homeFieldingStats.set(p.id, mkFieldingLine(p)));
  awayFielders.forEach(p => awayFieldingStats.set(p.id, mkFieldingLine(p)));

  const homePitcherLines: any[] = [mkPitcherState(homePitchers[0] || homeTeam.players[0], true)];
  const awayPitcherLines: any[] = [mkPitcherState(awayPitchers[0] || awayTeam.players[0], true)];

  const countRunners = () => (runners.first ? 1 : 0) + (runners.second ? 1 : 0) + (runners.third ? 1 : 0);

  const advanceRunners = (batterId: string, type: string, battingStats: Map<string, BatterLine>, runnerSpeed: Map<string, number>): number => {
    let runs = 0;
    const scoringRunners: string[] = [];

    // Helper: get runner's speed (default 50)
    const getSpeed = (id: string) => runnerSpeed.get(id) || 50;

    if (type === 'single') {
      // Runner on 3rd always scores
      if (runners.third) { scoringRunners.push(runners.third); runs++; runners.third = null; }
      // Runner on 2nd scores ~80% (almost always, even slow runners)
      if (runners.second) { if (Math.random() < 0.80) { scoringRunners.push(runners.second); runs++; runners.second = null; } else { runners.third = runners.second; runners.second = null; } }
      // Runner on 1st: fast runners go to 3rd on outfield single
      if (runners.first) {
        const speed = getSpeed(runners.first);
        const firstTo3rdChance = 0.10 + (speed / 100) * 0.40; // 10-50% based on speed
        if (Math.random() < firstTo3rdChance) runners.third = runners.first;
        else runners.second = runners.first;
        runners.first = null;
      }
      runners.first = batterId;
    } else if (type === 'double') {
      // Runners on 2nd and 3rd always score
      if (runners.third) { scoringRunners.push(runners.third); runs++; runners.third = null; }
      if (runners.second) { scoringRunners.push(runners.second); runs++; runners.second = null; }
      // Runner on 1st: fast runners score, slow runners stop at 3rd
      if (runners.first) {
        const speed = getSpeed(runners.first);
        const scoreChance = 0.35 + (speed / 100) * 0.45; // 35-80% based on speed
        if (Math.random() < scoreChance) { scoringRunners.push(runners.first); runs++; }
        else runners.third = runners.first;
        runners.first = null;
      }
      runners.second = batterId;
    } else if (type === 'triple') {
      if (runners.third) { scoringRunners.push(runners.third); runs++; }
      if (runners.second) { scoringRunners.push(runners.second); runs++; }
      if (runners.first) { scoringRunners.push(runners.first); runs++; }
      runners.first = null; runners.second = null; runners.third = batterId;
    } else if (type === 'walk') {
      if (runners.first && runners.second && runners.third) { scoringRunners.push(runners.third); runs++; runners.third = runners.second; runners.second = runners.first; runners.first = batterId; }
      else if (runners.first && runners.second) { runners.third = runners.second; runners.second = runners.first; runners.first = batterId; }
      else if (runners.first) { runners.second = runners.first; runners.first = batterId; }
      else runners.first = batterId;
    } else if (type === 'sac') {
      if (runners.third) { scoringRunners.push(runners.third); runs++; runners.third = null; }
      else if (runners.second) { runners.third = runners.second; runners.second = null; }
      if (runners.first) { runners.second = runners.first; runners.first = null; }
    }

    // Increment runs for each scoring runner
    for (const runnerId of scoringRunners) {
      const runnerStat = battingStats.get(runnerId);
      if (runnerStat) runnerStat.runs++;
    }

    return runs;
  };

  const getPitcher = (isHomeBatting: boolean) => isHomeBatting ? awayPitcherLines[awayPitcherLines.length - 1] : homePitcherLines[homePitcherLines.length - 1];

  const checkPitchingChange = (isHomeBatting: boolean, inning: number) => {
    const pitcher = getPitcher(isHomeBatting);
    const maxPitches = pitcher.is_starter ? 100 : 30;
    const maxE = pitcher.is_starter ? 100 : 50;
    const stam = pitcher?.player?.stamina || 50;
    const pct = pitcher.endurance_remaining / (maxE * (0.6 + (stam / 100) * 0.8));

    // Check if pitcher is getting shelled — always check, no minimum innings exception
    const isGettingShelled = pitcher.is_starter
      ? pitcher.runs_allowed >= 5
      : pitcher.runs_allowed >= 3;

    // Pull when: pitch count reached, endurance low, OR getting shelled
    const pullThreshold = pitcher.is_starter ? 0.35 : 0.20;
    const shouldPull = pitcher.pitches_thrown >= maxPitches || pct < pullThreshold || isGettingShelled;
    if (!shouldPull) return;

    // Starter minimum: try 5 innings unless getting shelled (5+ runs)
    if (pitcher.is_starter && pitcher.outs_recorded < 15 && pitcher.runs_allowed < 5) return;
    // Need a reliever — exclude starters from bullpen (they rest between starts)
    const bullpen = isHomeBatting ? awayPitchers : homePitchers;
    const used = new Set((isHomeBatting ? awayPitcherLines : homePitcherLines).map((p: any) => p.player.id));
    const avail = bullpen.filter(p => !used.has(p.id) && p.id !== pitcher.player.id && p.position === 'P' && !p.is_starter);
    if (avail.length === 0) {
      // Fallback: allow starters if no relievers left (emergency)
      const emergencyAvail = bullpen.filter(p => !used.has(p.id) && p.id !== pitcher.player.id);
      if (emergencyAvail.length === 0) return;
      const replacement = emergencyAvail.sort((a, b) => ((b.velocity || 0) + (b.control || 0)) - ((a.velocity || 0) + (a.control || 0)))[0];
      const newP = mkPitcherState(replacement, false);
      if (isHomeBatting) awayPitcherLines.push(newP); else homePitcherLines.push(newP);
      return;
    }
    const replacement = avail.sort((a, b) => ((b.velocity || 0) + (b.control || 0)) - ((a.velocity || 0) + (a.control || 0)))[0];
    const newP = mkPitcherState(replacement, false);
    if (isHomeBatting) awayPitcherLines.push(newP); else homePitcherLines.push(newP);
  };

  // Build speed lookup for runner advancement
  const runnerSpeed = new Map<string, number>();
  homeLineup.forEach(p => runnerSpeed.set(p.id, p.speed || 50));
  awayLineup.forEach(p => runnerSpeed.set(p.id, p.speed || 50));

  // Line score tracking
  const lineScore: { inning: number; away_runs: number; home_runs: number }[] = [];

  // Simulate innings
  let totalInnings = 9;
  for (let inning = 1; inning <= totalInnings; inning++) {
    // Track errors for earned run calculation
    let errorsThisInning = 0;
    // Track score before each half inning for line score
    const awayBefore = awayScore;
    const homeBefore = homeScore;

    for (const half of ['top', 'bottom'] as const) {
      const isHomeBatting = half === 'bottom';
      errorsThisInning = 0;

      // Skip bottom of 9th if home team is already winning
      if (inning === 9 && half === 'bottom' && homeScore > awayScore) {
        continue;
      }

      const lineup = isHomeBatting ? homeLineup : awayLineup;
      const bench = isHomeBatting ? homeBench : awayBench;
      const battingStats = isHomeBatting ? homeBatterStats : awayBatterStats;
      const fieldingStats = isHomeBatting ? awayFieldingStats : homeFieldingStats;
      let lineupIdx = ((inning - 1)) % lineup.length;
      runners.first = null; runners.second = null; runners.third = null;
      let outs = 0;

      // Defensive substitution in late innings when leading
      if (inning >= 8 && outs === 0) {
        const teamScore = isHomeBatting ? homeScore : awayScore;
        const oppScore = isHomeBatting ? awayScore : homeScore;
        tryDefensiveSub(lineup, bench, fieldingStats, battingStats, inning, teamScore, oppScore);
      }

      while (outs < 3) {
        // Only check for pitching change at start of inning (not mid-inning)
        if (outs === 0) checkPitchingChange(isHomeBatting, inning);
        
        // Pinch hit in late innings when trailing
        if (inning >= 7) {
          const teamScore = isHomeBatting ? homeScore : awayScore;
          const oppScore = isHomeBatting ? awayScore : homeScore;
          tryPinchHit(lineup, bench, battingStats, inning, isHomeBatting, teamScore, oppScore, lineupIdx);
        }
        
        const pitcher = getPitcher(isHomeBatting);
        const batter = lineup[lineupIdx % lineup.length];
        const bStat = battingStats.get(batter.id)!;
        const teamStrategy = isHomeBatting ? homeTeam.strategy : awayTeam.strategy;

        // === STEAL ATTEMPT ===
        // Check for steal before each at-bat (runners on 1st or 2nd, less than 2 outs)
        if (outs < 2) {
          const stealBase = teamStrategy.stealAttempts / 100; // 0-1
          // Base steal chance: ~3% per at-bat at default (50), scales up to ~8% at 100
          const stealChance = 0.01 + stealBase * 0.07;
          
          if (runners.first && !runners.second && Math.random() < stealChance) {
            const runnerId = runners.first;
            const speed = runnerSpeed.get(runnerId) || 50;
            const catcherArm = 50;
            const successRate = 0.55 + (speed - 50) / 200 - (catcherArm - 50) / 300;
            
            if (Math.random() < successRate) {
              runners.second = runnerId;
              runners.first = null;
              const rStat = battingStats.get(runnerId);
              if (rStat) rStat.stolen_bases++;
            } else {
              outs++;
              runners.first = null;
              const rStat = battingStats.get(runnerId);
              if (rStat) rStat.caught_stealing++;
            }
          } else if (runners.second && !runners.first && !runners.third && Math.random() < stealChance * 0.3) {
            const runnerId = runners.second;
            const speed = runnerSpeed.get(runnerId) || 50;
            const successRate = 0.45 + (speed - 50) / 250;
            
            if (Math.random() < successRate) {
              runners.third = runnerId;
              runners.second = null;
              const rStat = battingStats.get(runnerId);
              if (rStat) rStat.stolen_bases++;
            } else {
              outs++;
              runners.second = null;
              const rStat = battingStats.get(runnerId);
              if (rStat) rStat.caught_stealing++;
            }
          }
          
          if (outs >= 3) {
            lineupIdx++;
            continue;
          }
        }

        // === SACRIFICE BUNT ===
        // With runner on 1st/2nd, less than 2 outs — attempt bunt
        const buntBase = teamStrategy.sacrificeBunts / 100;
        const buntChance = 0.005 + buntBase * 0.08; // 0.5% at default, up to 8.5% at 100
        const hasRunnerForBunt = runners.first || runners.second;
        let isBunt = false;
        
        if (hasRunnerForBunt && outs < 2 && Math.random() < buntChance && batter.speed && batter.speed < 80) {
          isBunt = true;
          bStat.plate_appearances++;
          // Bunt success rate based on contact
          const buntSuccess = 0.65 + (batter.contact || 50) / 500;
          
          if (Math.random() < buntSuccess) {
            // Successful sacrifice — runner advances, batter out
            if (runners.third && runners.second) {
              runners.third = runners.second;
              runners.second = runners.first;
              runners.first = null;
            } else if (runners.second && !runners.third) {
              runners.third = runners.second;
              runners.second = runners.first;
              runners.first = null;
            } else if (runners.first) {
              runners.second = runners.first;
              runners.first = null;
            }
            bStat.sacrifice_hits++;
            outs++;
          } else {
            // Failed bunt — strike or foul out
            outs++;
          }
          lineupIdx++;
          continue;
        }

        // === HITTING APPROACH MODIFIER ===
        // Higher approach = more aggressive/power swing (more HR, more K)
        // Lower approach = contact swing (more balls in play, fewer HR)
        const approachMod = (teamStrategy.hittingApproach - 50) / 200; // -0.25 to +0.25

        // Simulate at-bat pitch by pitch
        let balls = 0, strikes = 0;
        while (balls < 4 && strikes < 3) {
          pitcher.pitches_thrown++;
          drainEndurance(pitcher, countRunners());
          const fatigue = getFatigue(pitcher);
          const vel = (pitcher.player.velocity || 50) * fatigue.v;
          const ctl = (pitcher.player.control || 50) * fatigue.c;
          const mov = (pitcher.player.movement || 50) * fatigue.m;
          const inZone = Math.random() * 100 < (ctl + 15);

          // Hidden attribute modifiers
          const hasRISP = runners.second !== null || runners.third !== null;
          const clutchMod = hasRISP ? (batter.clutch || 50 - 50) / 200 : 0; // ±5% in clutch spots
          const intelMod = (batter.intelligence || 50 - 50) / 400; // ±2.5% discipline
          const consistency = batter.consistency || 50;

          // Batter swing decision (intelligence affects discipline)
          const swingChance = inZone
            ? 0.55 + (batter.discipline || 50) / 500 + (batter.contact || 50) / 500 + intelMod
            : 0.15 + (batter.contact || 50) / 1000 - (batter.discipline || 50) / 500 - intelMod;
          const swings = Math.random() < swingChance;

          if (!swings) {
            // Didn't swing — called strike or ball
            if (inZone) strikes++; else balls++;
            continue;
          }

          // Batter swings — contact check
          const contactChance = inZone
            ? 0.75 + (batter.contact || 50) / 300 - mov / 600
            : 0.37 + (batter.contact || 50) / 400 - mov / 400;
          const makesContact = Math.random() < Math.min(0.95, Math.max(0.15, contactChance));

          if (!makesContact) {
            // Swing and miss — always a strike
            strikes++;
            continue;
          }

          // Contact made — check for foul ball (extends count)
          // Foul chance — realistic
          const batterContact = batter.contact || 50;
          const foulChance = inZone
            ? 0.30 + (batterContact / 100) * 0.30   // 30-60% based on contact
            : 0.45 + (batterContact / 100) * 0.25;   // 45-70% on out-of-zone
          if (Math.random() < foulChance) {
            // Foul ball — only adds a strike if less than 2 strikes
            if (strikes < 2) strikes++;
            // With 2 strikes, foul just extends the at-bat (doesn't add strike)
            // High-contact batters can foul off many pitches in a row
            continue;
          }

          // Fair ball — determine outcome and break out of pitch loop
          const quality = (batter.power || 50) * 0.4 + (batter.contact || 50) * 0.35 + (batter.speed || 50) * 0.1 + (100 - mov) * 0.15;
          
          // HR check — independent of the roll (prevents consistency from suppressing HRs)
          // HR check — power is primary, discipline helps (waits for pitch), pitcher attributes matter
          // Power pitchers give up MORE HRs (harder contact when they miss), control pitchers give up fewer
          const pitcherVel = pitcher.player.velocity || 50;
          const pitcherCtl = pitcher.player.control || 50;
          const hrRate = 0.02 
            + (batter.power || 50) / 100 * 0.04 
            + (batter.discipline || 50) / 100 * 0.015 
            + pitcherVel / 100 * 0.01       // power pitcher gives up more HRs
            - pitcherCtl / 100 * 0.012      // control pitcher gives up fewer
            + clutchMod * 0.01;
          let outcome: string;
          if (Math.random() < hrRate) {
            outcome = 'home_run';
          } else {
            // Remaining fair ball outcomes (91-97% of fair balls)
            const consistencyFactor = 1 - ((consistency || 50) / 200);
            const roll = Math.random() * 100 * consistencyFactor + (100 * (1 - consistencyFactor) / 2);
            if (roll < 0.5 + (batter.speed || 50) / 100 * 1.5) outcome = 'triple';
            else if (roll < 0.5 + (batter.speed || 50) / 100 * 1.5 + 5 + quality / 100 * 5) outcome = 'double';
            else if (roll < 0.5 + (batter.speed || 50) / 100 * 1.5 + 5 + quality / 100 * 5 + 15 + (batter.contact || 50) / 100 * 16 - mov / 100 * 3) outcome = 'single';
            else if (roll < 0.5 + (batter.speed || 50) / 100 * 1.5 + 5 + quality / 100 * 5 + 15 + (batter.contact || 50) / 100 * 16 - mov / 100 * 3 + 1.8) outcome = 'error';
            else { const oRoll = Math.random(); outcome = oRoll < 0.45 ? 'groundout' : oRoll < 0.80 ? 'flyout' : oRoll < 0.93 ? 'lineout' : 'popout'; }
          }

          // Apply outcome
          bStat.plate_appearances++;
          let runsScored = 0;
          if (outcome === 'single') { bStat.at_bats++; bStat.hits++; if (isHomeBatting) homeHits++; else awayHits++; runsScored = advanceRunners(batter.id, 'single', battingStats, runnerSpeed); pitcher.hits_allowed++; }
          else if (outcome === 'double') { bStat.at_bats++; bStat.hits++; bStat.doubles++; if (isHomeBatting) homeHits++; else awayHits++; runsScored = advanceRunners(batter.id, 'double', battingStats, runnerSpeed); pitcher.hits_allowed++; }
          else if (outcome === 'triple') { bStat.at_bats++; bStat.hits++; bStat.triples++; if (isHomeBatting) homeHits++; else awayHits++; runsScored = advanceRunners(batter.id, 'triple', battingStats, runnerSpeed); pitcher.hits_allowed++; }
          else if (outcome === 'home_run') {
            bStat.at_bats++; bStat.hits++; bStat.home_runs++; bStat.runs++;
            if (isHomeBatting) homeHits++; else awayHits++;
            runsScored = 1 + countRunners();
            // Increment runners' runs stat for all who scored
            if (runners.third) { const rs = battingStats.get(runners.third); if (rs) rs.runs++; }
            if (runners.second) { const rs = battingStats.get(runners.second); if (rs) rs.runs++; }
            if (runners.first) { const rs = battingStats.get(runners.first); if (rs) rs.runs++; }
            runners.first = null; runners.second = null; runners.third = null;
            pitcher.hits_allowed++; pitcher.home_runs_allowed++;
          }
          else if (outcome === 'walk') { bStat.walks++; pitcher.walks_allowed++; runsScored = advanceRunners(batter.id, 'walk', battingStats, runnerSpeed); }
          else if (outcome === 'error') { bStat.at_bats++; if (isHomeBatting) awayErrors++; else homeErrors++; runners.first = batter.id; errorsThisInning++; }
          else if (outcome === 'groundout') { bStat.at_bats++; outs++; if (runners.first && outs < 3 && Math.random() < 0.25) { bStat.grounded_into_dp++; runners.first = null; outs++; } }
          else if (outcome === 'flyout') {
            bStat.at_bats++; outs++;
            if (runners.third && outs < 3) {
              runsScored = 1;
              // Runner on 3rd scores on sac fly
              const scoringRunner = battingStats.get(runners.third);
              if (scoringRunner) scoringRunner.runs++;
              runners.third = null;
              bStat.sacrifice_flies++;
            }
          }
          else if (outcome === 'strikeout') { bStat.at_bats++; bStat.strikeouts++; outs++; pitcher.strikeouts++; }
          else { bStat.at_bats++; outs++; }

          if (runsScored > 0) {
            bStat.rbi += runsScored;
            if (isHomeBatting) { homeScore += runsScored; } else { awayScore += runsScored; }
            pitcher.runs_allowed += runsScored;
            // Track run event for go-ahead run determination
            for (let rr = 0; rr < runsScored; rr++) {
              runEvents.push({ pitcherId: pitcher.player.id, isHomeScoring: isHomeBatting, homeAfter: homeScore, awayAfter: awayScore });
            }
            // Earned runs: only count if no errors this inning
            if (errorsThisInning === 0) {
              pitcher.earned_runs += runsScored;
            }
          }
          break; // at-bat resolved
        }
        // If count resolved without contact (walk or strikeout)
        if (balls >= 4) {
          bStat.plate_appearances++; bStat.walks++;
          getPitcher(isHomeBatting).walks_allowed++;
          advanceRunners(batter.id, 'walk', battingStats, runnerSpeed);
        } else if (strikes >= 3) {
          bStat.plate_appearances++; bStat.at_bats++; bStat.strikeouts++; outs++;
          getPitcher(isHomeBatting).strikeouts++;
        }
        lineupIdx++;
      }
      getPitcher(isHomeBatting).outs_recorded += 3;
    }
    // Record line score for this inning
    lineScore.push({
      inning,
      away_runs: awayScore - awayBefore,
      home_runs: homeScore - homeBefore,
    });
  }

  // Extra innings — both teams always bat, game ends when scores differ after complete inning
  let extraInning = 10;
  while (homeScore === awayScore) {
    totalInnings++;
    const awayBeforeExtra = awayScore;
    const homeBeforeExtra = homeScore;

    // TOP of extra inning (away team bats)
    runners.first = null; runners.second = null; runners.third = null;
    let outs = 0;
    let awayLineupIdx = 0;
    let errorsThisInning = 0;
    while (outs < 3) {
      if (outs === 0) checkPitchingChange(false, extraInning);
      const pitcher = getPitcher(false);
      const batter = awayLineup[awayLineupIdx % awayLineup.length];
      const bStat = awayBatterStats.get(batter.id)!;
      bStat.plate_appearances++; bStat.at_bats++;
      const roll = Math.random();
      if (roll < 0.08) { bStat.walks++; pitcher.walks_allowed++; advanceRunners(batter.id, 'walk', awayBatterStats, runnerSpeed); }
      else if (roll < 0.30) { bStat.strikeouts++; outs++; pitcher.strikeouts++; }
      else if (roll < 0.38) { bStat.hits++; awayHits++; const r = advanceRunners(batter.id, 'single', awayBatterStats, runnerSpeed); pitcher.hits_allowed++; if (r) { bStat.rbi += r; awayScore += r; pitcher.runs_allowed += r; runEvents.push({ pitcherId: pitcher.player.id, isHomeScoring: false, homeAfter: homeScore, awayAfter: awayScore }); if (errorsThisInning === 0) pitcher.earned_runs += r; } }
      else if (roll < 0.42) { bStat.hits++; bStat.doubles++; awayHits++; const r = advanceRunners(batter.id, 'double', awayBatterStats, runnerSpeed); pitcher.hits_allowed++; if (r) { bStat.rbi += r; awayScore += r; pitcher.runs_allowed += r; runEvents.push({ pitcherId: pitcher.player.id, isHomeScoring: false, homeAfter: homeScore, awayAfter: awayScore }); if (errorsThisInning === 0) pitcher.earned_runs += r; } }
      else if (roll < 0.43) {
        bStat.hits++; bStat.home_runs++; bStat.runs++; awayHits++;
        const r = 1 + countRunners();
        // Increment runners' runs
        if (runners.third) { const rs = awayBatterStats.get(runners.third); if (rs) rs.runs++; }
        if (runners.second) { const rs = awayBatterStats.get(runners.second); if (rs) rs.runs++; }
        if (runners.first) { const rs = awayBatterStats.get(runners.first); if (rs) rs.runs++; }
        runners.first = null; runners.second = null; runners.third = null;
        bStat.rbi += r; awayScore += r; pitcher.hits_allowed++; pitcher.home_runs_allowed++; pitcher.runs_allowed += r; if (errorsThisInning === 0) pitcher.earned_runs += r;
      }
      else outs++;
      pitcher.pitches_thrown++; pitcher.batters_faced++;
      awayLineupIdx++;
    }
    getPitcher(false).outs_recorded += 3;

    // After top of extra inning: if home team is already winning, game over (no bottom needed)
    if (homeScore > awayScore) break;

    // BOTTOM of extra inning (home team bats — chance to tie or walk-off)
    runners.first = null; runners.second = null; runners.third = null;
    outs = 0;
    let homeLineupIdx = 0;
    errorsThisInning = 0;
    while (outs < 3) {
      // Walk-off check: if home team already has the lead, stop
      if (homeScore > awayScore) break;

      if (outs === 0) checkPitchingChange(true, extraInning);
      const pitcher = getPitcher(true);
      const batter = homeLineup[homeLineupIdx % homeLineup.length];
      const bStat = homeBatterStats.get(batter.id)!;
      bStat.plate_appearances++; bStat.at_bats++;
      const roll = Math.random();
      if (roll < 0.08) { bStat.walks++; pitcher.walks_allowed++; advanceRunners(batter.id, 'walk', homeBatterStats, runnerSpeed); }
      else if (roll < 0.30) { bStat.strikeouts++; outs++; pitcher.strikeouts++; }
      else if (roll < 0.38) { bStat.hits++; homeHits++; const r = advanceRunners(batter.id, 'single', homeBatterStats, runnerSpeed); pitcher.hits_allowed++; if (r) { bStat.rbi += r; homeScore += r; pitcher.runs_allowed += r; runEvents.push({ pitcherId: pitcher.player.id, isHomeScoring: true, homeAfter: homeScore, awayAfter: awayScore }); if (errorsThisInning === 0) pitcher.earned_runs += r; } }
      else if (roll < 0.42) { bStat.hits++; bStat.doubles++; homeHits++; const r = advanceRunners(batter.id, 'double', homeBatterStats, runnerSpeed); pitcher.hits_allowed++; if (r) { bStat.rbi += r; homeScore += r; pitcher.runs_allowed += r; runEvents.push({ pitcherId: pitcher.player.id, isHomeScoring: true, homeAfter: homeScore, awayAfter: awayScore }); if (errorsThisInning === 0) pitcher.earned_runs += r; } }
      else if (roll < 0.43) {
        bStat.hits++; bStat.home_runs++; bStat.runs++; homeHits++;
        const r = 1 + countRunners();
        // Increment runners' runs
        if (runners.third) { const rs = homeBatterStats.get(runners.third); if (rs) rs.runs++; }
        if (runners.second) { const rs = homeBatterStats.get(runners.second); if (rs) rs.runs++; }
        if (runners.first) { const rs = homeBatterStats.get(runners.first); if (rs) rs.runs++; }
        runners.first = null; runners.second = null; runners.third = null;
        bStat.rbi += r; homeScore += r; pitcher.hits_allowed++; pitcher.home_runs_allowed++; pitcher.runs_allowed += r; if (errorsThisInning === 0) pitcher.earned_runs += r;
      }
      else outs++;
      pitcher.pitches_thrown++; pitcher.batters_faced++;
      homeLineupIdx++;
    }
    getPitcher(true).outs_recorded += 3;

    // Record line score for this extra inning
    lineScore.push({
      inning: extraInning,
      away_runs: awayScore - awayBeforeExtra,
      home_runs: homeScore - homeBeforeExtra,
    });

    // After complete inning: if scores differ, game is over
    if (homeScore !== awayScore) break;

    extraInning++;
    if (extraInning > 30) {
      // Extreme safety — force a winner by giving home team a run
      homeScore++;
      break;
    }
  }

  // Finalize pitcher IPs
  const finalizePitcher = (p: any): PitcherLine => {
    const ip = Math.floor(p.outs_recorded / 3) + (p.outs_recorded % 3) / 10;
    return {
      player_id: p.player.id, name: p.player.name, is_starter: p.is_starter,
      innings_pitched: ip, batters_faced: p.batters_faced, pitches_thrown: p.pitches_thrown,
      hits_allowed: p.hits_allowed, runs_allowed: p.runs_allowed, earned_runs: p.earned_runs,
      walks_allowed: p.walks_allowed, strikeouts: p.strikeouts, home_runs_allowed: p.home_runs_allowed,
      win: p.win, loss: p.loss, save: p.save,
    };
  };

  // W/L decisions (proper MLB rules) — only if not a tie
  if (homeScore === awayScore) {
    // Tie game (shouldn't happen but handle gracefully)
    return {
      game_id: gameId, home_team_id: '', away_team_id: '',
      home_score: homeScore, away_score: awayScore,
      home_hits: homeHits, away_hits: awayHits,
      home_errors: homeErrors, away_errors: awayErrors,
      innings: totalInnings,
    line_score: lineScore,
      home_pitchers: homePitcherLines.map(finalizePitcher),
      away_pitchers: awayPitcherLines.map(finalizePitcher),
      home_batters: Array.from(homeBatterStats.values()),
      away_batters: Array.from(awayBatterStats.values()),
      home_fielding: Array.from(homeFieldingStats.values()),
      away_fielding: Array.from(awayFieldingStats.values()),
      winning_pitcher_id: null, losing_pitcher_id: null, save_pitcher_id: null,
    };
  }
  const winner = homeScore > awayScore ? 'home' : 'away';
  const winPitchers = winner === 'home' ? homePitcherLines : awayPitcherLines;
  const losePitchers = winner === 'home' ? awayPitcherLines : homePitcherLines;
  let winId: string | null = null, lossId: string | null = null, saveId: string | null = null;

  // WIN: Starter if 5+ IP, else best reliever (fewest runs allowed), else first reliever
  const starter = winPitchers.find((p: any) => p.is_starter);
  if (starter && starter.outs_recorded >= 15 && starter.runs_allowed <= 3) {
    // Starter went 5+ innings and didn't get shelled — give them the win
    starter.win = true;
    winId = starter.player.id;
  } else {
    // Find best reliever (0 ER preferred, then fewest runs)
    const relievers = winPitchers.filter((p: any) => !p.is_starter);
    if (relievers.length > 0) {
      relievers.sort((a: any, b: any) => a.runs_allowed - b.runs_allowed || b.outs_recorded - a.outs_recorded);
      relievers[0].win = true;
      winId = relievers[0].player.id;
    } else if (winPitchers.length > 0) {
      winPitchers[0].win = true;
      winId = winPitchers[0].player.id;
    }
  }

  // LOSS: Pitcher responsible for the go-ahead run
  // The go-ahead run is the run that gave the winning team a lead they never lost
  if (losePitchers.length > 0) {
    let lossIdFound = false;
    
    if (runEvents.length > 0) {
      // Find the go-ahead run: last run by the winning team that created a lead
      const winnerIsHome = winner === 'home';
      // Walk backwards through runs — find the last run by the winner that gave them the lead
      for (let i = runEvents.length - 1; i >= 0; i--) {
        const re = runEvents[i];
        if (re.isHomeScoring === winnerIsHome) {
          // This is a run by the winning team
          const winnerScore = winnerIsHome ? re.homeAfter : re.awayAfter;
          const loserScore = winnerIsHome ? re.awayAfter : re.homeAfter;
          if (winnerScore > loserScore) {
            // This run put the winner ahead — find the pitcher
            const lp = losePitchers.find((p: any) => p.player.id === re.pitcherId);
            if (lp) {
              lp.loss = true;
              lossId = re.pitcherId;
              lossIdFound = true;
              break;
            }
          }
        }
      }
    }
    
    if (!lossIdFound) {
      // Fallback: starter gets the loss
      const loseStarter = losePitchers.find((p: any) => p.is_starter) || losePitchers[0];
      loseStarter.loss = true;
      lossId = loseStarter.player.id;
    }
  }

  // Save: close game (1-3 run lead), last pitcher, not the winner, pitched 1+ inning
  const lead = winner === 'home' ? homeScore - awayScore : awayScore - homeScore;
  if (lead >= 1 && lead <= 3 && winPitchers.length > 1) {
    const last = winPitchers[winPitchers.length - 1];
    if (!last.win && last.outs_recorded >= 3 && last.runs_allowed === 0) {
      last.save = true;
      saveId = last.player.id;
    }
  }

  return {
    game_id: gameId, home_team_id: '', away_team_id: '',
    home_score: homeScore, away_score: awayScore,
    home_hits: homeHits, away_hits: awayHits,
    home_errors: homeErrors, away_errors: awayErrors,
    innings: totalInnings,
    line_score: lineScore,
    home_pitchers: homePitcherLines.map(finalizePitcher),
    away_pitchers: awayPitcherLines.map(finalizePitcher),
    home_batters: Array.from(homeBatterStats.values()),
    away_batters: Array.from(awayBatterStats.values()),
    home_fielding: Array.from(homeFieldingStats.values()),
    away_fielding: Array.from(awayFieldingStats.values()),
    winning_pitcher_id: winId, losing_pitcher_id: lossId, save_pitcher_id: saveId,
  };
}

// Simulate a single game
/**
 * Select the starting pitcher based on rotation order and rest days.
 * Pitchers need at least 4 days rest (last_game_day + 5 <= currentDay).
 * If rotationOrder is provided (array of player IDs), use that order.
 * Otherwise, fall back to position-based selection (SP > RP > CL).
 */
function selectStartingPitcher(pitchers: any[], currentDay: number, rotationOrder: string[] = []): { starter: any; remaining: any[] } {
  const MIN_REST_DAYS = 4; // 4 days off = pitch every 5th day

  const isAvailable = (p: any) => {
    if (!p.last_game_day) return true;
    return (currentDay - p.last_game_day) >= MIN_REST_DAYS;
  };

  // If we have a stored rotation order, use it
  if (rotationOrder.length > 0) {
    for (const pid of rotationOrder) {
      const pitcher = pitchers.find(p => p.id === pid);
      if (pitcher && isAvailable(pitcher)) {
        const remaining = pitchers.filter(p => p.id !== pitcher.id);
        return { starter: pitcher, remaining };
      }
    }
    // All rotation pitchers tired — fall through to emergency
  }

  // Fallback: separate by role and pick most rested
  const starters = pitchers.filter(p => p.position === 'P' && p.is_starter);
  const relievers = pitchers.filter(p => p.position === 'P' && !p.is_starter);

  const availableStarters = starters.filter(isAvailable);
  if (availableStarters.length > 0) {
    const starter = availableStarters.sort((a, b) => {
      const aLast = a.last_game_day || 0;
      const bLast = b.last_game_day || 0;
      return aLast - bLast;
    })[0];
    const remaining = pitchers.filter(p => p.id !== starter.id);
    return { starter, remaining };
  }

  // Emergency: use most rested reliever
  const availableRelievers = relievers.filter(isAvailable);
  if (availableRelievers.length > 0) {
    const starter = availableRelievers.sort((a, b) => {
      const aLast = a.last_game_day || 0;
      const bLast = b.last_game_day || 0;
      return aLast - bLast;
    })[0];
    const remaining = pitchers.filter(p => p.id !== starter.id);
    return { starter, remaining };
  }

  // Absolute emergency — everyone is tired
  const starter = [...pitchers].sort((a, b) => {
    const aLast = a.last_game_day || 0;
    const bLast = b.last_game_day || 0;
    return aLast - bLast;
  })[0];
  const remaining = pitchers.filter(p => p.id !== starter.id);
  return { starter, remaining };
}

/**
 * Mark pitchers who appeared in the game with last_game_day and increment games_played_streak.
 */
async function updatePitcherUsage(pitcherIds: string[], currentDay: number): Promise<void> {
  if (!isSupabaseConfigured() || !supabase) return;

  for (const pid of pitcherIds) {
    // Fetch current values
    const { data: player } = await supabase
      .from('players')
      .select('games_played_streak')
      .eq('id', pid)
      .single();

    await supabase.from('players').update({
      last_game_day: currentDay,
      games_played_streak: (player?.games_played_streak || 0) + 1,
    }).eq('id', pid);
  }
}

// ============================================================
// V2 ENGINE ADAPTER
// ============================================================

/** Convert DB player record to V2 SimPlayer */
function dbPlayerToV2Player(db: any): V2SimPlayer {
  return {
    id: db.id,
    first_name: db.first_name || '',
    last_name: db.last_name || '',
    position: db.position || 'OF',
    team_id: db.team_id || '',
    is_hitter: db.is_hitter === true,
    batting_hand: db.batting_hand || 'R',
    throwing_hand: db.throwing_hand || 'R',
    // Hitter attrs (default to 50 if missing)
    contact: db.contact ?? 50,
    battingAttr: db.battingAttr ?? db.batting_average ?? db.power ?? 50,
    power: db.power ?? 50,
    discipline: db.discipline ?? 50,
    speed: db.speed ?? 50,
    range: db.range ?? 50,
    glove: db.glove ?? 50,
    arm: db.arm ?? 50,
    // Pitcher attrs (default to 50 if missing)
    velocity: db.velocity ?? 50,
    movement: db.movement ?? 50,
    control: db.control ?? 50,
    deception: db.deception ?? 50,
    hold_runner: db.hold_runner ?? 50,
    // Hidden attrs
    clutch: db.clutch ?? 50,
    consistency: db.consistency ?? 50,
    durability: db.durability ?? 50,
    intelligence: db.intelligence ?? 50,
    // State
    current_stamina: db.current_stamina ?? 100,
    last_game_day: db.last_game_day ?? 0,
  };
}

/** Convert V2 GameResult to legacy GameBoxScore for saveBoxScoreStats compatibility */
function v2ResultToBoxScore(result: GameResult, gameId: string, homeTeamId: string, awayTeamId: string): GameBoxScore {
  return {
    game_id: gameId,
    home_team_id: homeTeamId,
    away_team_id: awayTeamId,
    home_score: result.homeScore,
    away_score: result.awayScore,
    home_hits: result.homeHits,
    away_hits: result.awayHits,
    home_errors: result.homeErrors,
    away_errors: result.awayErrors,
    innings: result.innings,
    line_score: result.lineScore.map(l => ({ inning: l.inning, away_runs: l.awayRuns, home_runs: l.homeRuns })),
    home_pitchers: result.homePitchers.map((p: any) => ({ player_id: p.playerId, ...p })) as any,
    away_pitchers: result.awayPitchers.map((p: any) => ({ player_id: p.playerId, ...p })) as any,
    home_batters: result.homeBatters.map((b: any) => ({ player_id: b.playerId, ...b })) as any,
    away_batters: result.awayBatters.map((b: any) => ({ player_id: b.playerId, ...b })) as any,
    home_fielding: [],
    away_fielding: [],
    winning_pitcher_id: result.winningPitcherId,
    losing_pitcher_id: result.losingPitcherId,
    save_pitcher_id: result.savePitcherId,
  };
}

export async function simulateAndSaveGame(game: ScheduledGameFromDB): Promise<{ success: boolean; homeScore: number; awayScore: number; winner: string }> {
  const homeTeam = await getTeamById(game.home_team_id);
  const awayTeam = await getTeamById(game.away_team_id);
  if (!homeTeam || !awayTeam) { console.error(`❌ Could not find teams for game ${game.id}`); return { success: false, homeScore: 0, awayScore: 0, winner: '' }; }

  // Load team strategies
  let homeStrategy: TeamStrategy = { ...DEFAULT_STRATEGY };
  let awayStrategy: TeamStrategy = { ...DEFAULT_STRATEGY };
  if (supabase) {
    const { data: homeStratData } = await supabase.from('teams').select('strategy').eq('id', game.home_team_id).single();
    const { data: awayStratData } = await supabase.from('teams').select('strategy').eq('id', game.away_team_id).single();
    homeStrategy = { ...DEFAULT_STRATEGY, ...(homeStratData?.strategy || {}) };
    awayStrategy = { ...DEFAULT_STRATEGY, ...(awayStratData?.strategy || {}) };
  }

  const homePlayersDB = await getTeamPlayers(game.home_team_id);
  const awayPlayersDB = await getTeamPlayers(game.away_team_id);

  // Convert to V2 SimPlayer format and use V2 engine
  const homeV2Players = homePlayersDB.map(dbPlayerToV2Player);
  const awayV2Players = awayPlayersDB.map(dbPlayerToV2Player);

  const homeTeamInfo = { id: homeTeam.id, city: homeTeam.city, name: homeTeam.name, strategy: homeStrategy as V2TeamStrategy };
  const awayTeamInfo = { id: awayTeam.id, city: awayTeam.city, name: awayTeam.name, strategy: awayStrategy as V2TeamStrategy };

  console.log(`🎮 V2 Engine: ${homeTeam.name} vs ${awayTeam.name} (Day ${game.game_day})`);

  const v2Result = simulateGameV2(homeV2Players, awayV2Players, homeTeamInfo, awayTeamInfo, game.id, game.game_day);

  // Convert V2 result to legacy format for existing save functions
  const boxScore = v2ResultToBoxScore(v2Result, game.id, game.home_team_id, game.away_team_id);

  const homeScore = boxScore.home_score;
  const awayScore = boxScore.away_score;
  const winner = homeScore > awayScore ? game.home_team_id : game.away_team_id;

  if (isSupabaseConfigured() && supabase) {
    // DISABLED: Server-side sim-tick + write_sim_results RPC is the sole authority
    // for writing game results. Frontend sim writes were overwriting correct scores.
    console.warn('⛔ Frontend sim write BLOCKED — server-side sim-tick is the authority');
    // await supabase.from('league_schedule').update({ status: 'completed', home_score: homeScore, away_score: awayScore, winner, line_score: boxScore.line_score }).eq('id', game.id);
    console.warn('⛔ Frontend sim write BLOCKED — server-side sim-tick is the authority');
    // await updateStandings(game.home_team_id, game.away_team_id, homeScore, awayScore, winner);
    // try {
    //   console.log(`📊 V2 stats for game ${game.id}: ${boxScore.home_batters.length} home batters, ${boxScore.away_batters.length} away batters, ${v2Result.atBats.length} at-bats`);
    //   await saveBoxScoreStats(boxScore, game);
    // } catch (err) { console.error('⚠️ Stats save error (non-fatal):', err); }
    // await updatePitcherUsage(allPitcherIds, game.game_day);

    // Update pitcher usage (last_game_day, games_played_streak)
    const allPitcherIds = [
      ...boxScore.home_pitchers.map(p => p.player_id),
      ...boxScore.away_pitchers.map(p => p.player_id),
    ];
    await updatePitcherUsage(allPitcherIds, game.game_day);
  }

  console.log(`⚾ ${homeTeam.name} ${homeScore} - ${awayScore} ${awayTeam.name} (${boxScore.innings} inn)`);
  return { success: true, homeScore, awayScore, winner };
}

// Save box score stats to database
async function saveBoxScoreStats(boxScore: GameBoxScore, game: ScheduledGameFromDB): Promise<void> {
  if (!isSupabaseConfigured() || !supabase) return;
  
  // Get current season year
  let seasonYear = 1900;
  try {
    const { data: simState } = await supabase.from('simulation_state').select('season_year').eq('id', 'global').single();
    if (simState?.season_year) seasonYear = simState.season_year;
  } catch (e) {}
  
  const allStats: any[] = [];
  
  // Save batter stats (home)
  for (const batter of boxScore.home_batters) {
    allStats.push({
      game_id: game.id,
      player_id: batter.player_id,
      team_id: game.home_team_id,
      league_id: game.league_id,
      game_day: game.game_day,
      season_year: seasonYear,
      is_pitcher: false,
      plate_appearances: batter.plate_appearances,
      at_bats: batter.at_bats,
      runs: batter.runs,
      hits: batter.hits,
      doubles: batter.doubles,
      triples: batter.triples,
      home_runs: batter.home_runs,
      rbi: batter.rbi,
      walks: batter.walks,
      strikeouts: batter.strikeouts,
      stolen_bases: batter.stolen_bases, caught_stealing: batter.caught_stealing || 0,
      hit_by_pitch: batter.hit_by_pitch,
      sacrifice_flies: batter.sacrifice_flies,
      grounded_into_dp: batter.grounded_into_dp,
      innings_pitched: 0,
      hits_allowed: 0,
      runs_allowed: 0,
      earned_runs: 0,
      walks_allowed: 0,
      strikeouts_pitched: 0,
      home_runs_allowed: 0,
      pitches_thrown: 0,
      win: false,
      loss: false,
      save: false,
    });
  }
  
  // Save batter stats (away)
  for (const batter of boxScore.away_batters) {
    allStats.push({
      game_id: game.id,
      player_id: batter.player_id,
      team_id: game.away_team_id,
      league_id: game.league_id,
      game_day: game.game_day,
      season_year: seasonYear,
      is_pitcher: false,
      plate_appearances: batter.plate_appearances,
      at_bats: batter.at_bats,
      runs: batter.runs,
      hits: batter.hits,
      doubles: batter.doubles,
      triples: batter.triples,
      home_runs: batter.home_runs,
      rbi: batter.rbi,
      walks: batter.walks,
      strikeouts: batter.strikeouts,
      stolen_bases: batter.stolen_bases, caught_stealing: batter.caught_stealing || 0,
      hit_by_pitch: batter.hit_by_pitch,
      sacrifice_flies: batter.sacrifice_flies,
      grounded_into_dp: batter.grounded_into_dp,
      innings_pitched: 0,
      hits_allowed: 0,
      runs_allowed: 0,
      earned_runs: 0,
      walks_allowed: 0,
      strikeouts_pitched: 0,
      home_runs_allowed: 0,
      pitches_thrown: 0,
      win: false,
      loss: false,
      save: false,
    });
  }
  
  // Save pitcher stats (home)
  for (const pitcher of boxScore.home_pitchers) {
    allStats.push({
      game_id: game.id,
      player_id: pitcher.player_id,
      team_id: game.home_team_id,
      league_id: game.league_id,
      game_day: game.game_day,
      season_year: seasonYear,
      is_pitcher: true,
      plate_appearances: 0,
      at_bats: 0,
      runs: 0,
      hits: 0,
      doubles: 0,
      triples: 0,
      home_runs: 0,
      rbi: 0,
      walks: 0,
      strikeouts: 0,
      stolen_bases: 0,
      innings_pitched: pitcher.innings_pitched,
      hits_allowed: pitcher.hits_allowed,
      runs_allowed: pitcher.runs_allowed,
      earned_runs: pitcher.earned_runs,
      walks_allowed: pitcher.walks_allowed,
      strikeouts_pitched: pitcher.strikeouts,
      home_runs_allowed: pitcher.home_runs_allowed,
      pitches_thrown: pitcher.pitches_thrown,
      win: pitcher.win,
      loss: pitcher.loss,
      save: pitcher.save,
    });
  }
  
  // Save pitcher stats (away)
  for (const pitcher of boxScore.away_pitchers) {
    allStats.push({
      game_id: game.id,
      player_id: pitcher.player_id,
      team_id: game.away_team_id,
      league_id: game.league_id,
      game_day: game.game_day,
      season_year: seasonYear,
      is_pitcher: true,
      plate_appearances: 0,
      at_bats: 0,
      runs: 0,
      hits: 0,
      doubles: 0,
      triples: 0,
      home_runs: 0,
      rbi: 0,
      walks: 0,
      strikeouts: 0,
      stolen_bases: 0,
      innings_pitched: pitcher.innings_pitched,
      hits_allowed: pitcher.hits_allowed,
      runs_allowed: pitcher.runs_allowed,
      earned_runs: pitcher.earned_runs,
      walks_allowed: pitcher.walks_allowed,
      strikeouts_pitched: pitcher.strikeouts,
      home_runs_allowed: pitcher.home_runs_allowed,
      pitches_thrown: pitcher.pitches_thrown,
      win: pitcher.win,
      loss: pitcher.loss,
      save: pitcher.save,
    });
  }
  
  // Batch insert all stats
  // Batch insert all stats
  if (allStats.length > 0) {
    console.log(`📊 Saving ${allStats.length} player stats for game ${game.id}`);
    const { error } = await supabase.from('player_game_stats').insert(allStats);
    if (error) {
      console.error('❌ Stats insert error:', JSON.stringify(error));
    }
  } else {
    console.warn('⚠️ No stats generated for game!');
  }
  
  // Update season stats (upsert cumulative)
  await updateSeasonStats(allStats, game.game_day);
}

// Update league standings
async function updateStandings(
  homeTeamId: string, 
  awayTeamId: string, 
  homeScore: number, 
  awayScore: number,
  winnerId: string
): Promise<void> {
  if (!isSupabaseConfigured() || !supabase) return;
  
  const isHomeWin = winnerId === homeTeamId;
  
  // Get current standings for home team
  const { data: homeStanding } = await supabase
    .from('league_standings')
    .select('*')
    .eq('team_id', homeTeamId)
    .single();
  
  // Get current standings for away team
  const { data: awayStanding } = await supabase
    .from('league_standings')
    .select('*')
    .eq('team_id', awayTeamId)
    .single();
  
  if (homeStanding) {
    await supabase.from('league_standings').update({
      wins: (homeStanding.wins || 0) + (isHomeWin ? 1 : 0),
      losses: (homeStanding.losses || 0) + (isHomeWin ? 0 : 1),
      runs_scored: (homeStanding.runs_scored || 0) + homeScore,
      runs_allowed: (homeStanding.runs_allowed || 0) + awayScore
    }).eq('team_id', homeTeamId);
  }
  
  if (awayStanding) {
    await supabase.from('league_standings').update({
      wins: (awayStanding.wins || 0) + (!isHomeWin ? 1 : 0),
      losses: (awayStanding.losses || 0) + (!isHomeWin ? 0 : 1),
      runs_scored: (awayStanding.runs_scored || 0) + awayScore,
      runs_allowed: (awayStanding.runs_allowed || 0) + homeScore
    }).eq('team_id', awayTeamId);
  }
}

// Update cumulative season stats from per-game stats
async function updateSeasonStats(gameStats: any[], currentDay: number): Promise<void> {
  console.log(`📊 updateSeasonStats called with ${gameStats?.length || 0} stats`);
  if (!isSupabaseConfigured() || !supabase) return;
  
  for (const stat of gameStats) {
    // Simple approach: try to find existing, then update or insert
    let existing: any = null;
    try {
      const { data } = await supabase
        .from('player_season_stats')
        .select('player_id, games, plate_appearances, at_bats, runs, hits, doubles, triples, home_runs, rbi, walks, strikeouts, stolen_bases, avg, wins, losses, innings_pitched, hits_allowed, runs_allowed, earned_runs, walks_allowed, strikeouts_pitched, home_runs_allowed, pitches_thrown, era, team_id, league_id, season_day, season_year')
        .eq('player_id', stat.player_id)
        .limit(1);
      if (data && data.length > 0) existing = data[0];
    } catch (e) {
      console.error('❌ Error checking existing stat:', e);
    }
    
    if (existing) {
      // UPDATE: add to existing values
      const updateData: any = {
        games: (existing.games || 0) + 1,
        season_year: existing.season_year || stat.season_year || new Date().getFullYear(),
        updated_at: new Date().toISOString(),
      };
      
      if (stat.is_pitcher) {
        if (stat.win) updateData.wins = (existing.wins || 0) + 1;
        if (stat.loss) updateData.losses = (existing.losses || 0) + 1;
        updateData.innings_pitched = parseFloat(String(existing.innings_pitched || 0)) + (stat.innings_pitched || 0);
        updateData.hits_allowed = (existing.hits_allowed || 0) + (stat.hits_allowed || 0);
        updateData.runs_allowed = (existing.runs_allowed || 0) + (stat.runs_allowed || 0);
        updateData.earned_runs = (existing.earned_runs || 0) + (stat.earned_runs || 0);
        updateData.walks_allowed = (existing.walks_allowed || 0) + (stat.walks_allowed || 0);
        updateData.strikeouts_pitched = (existing.strikeouts_pitched || 0) + (stat.strikeouts_pitched || 0);
        updateData.home_runs_allowed = (existing.home_runs_allowed || 0) + (stat.home_runs_allowed || 0);
        updateData.pitches_thrown = (existing.pitches_thrown || 0) + (stat.pitches_thrown || 0);
        if (updateData.innings_pitched > 0) {
          updateData.era = ((updateData.earned_runs * 9) / updateData.innings_pitched).toFixed(2);
        }
      } else {
        updateData.plate_appearances = (existing.plate_appearances || 0) + (stat.plate_appearances || 0);
        updateData.at_bats = (existing.at_bats || 0) + (stat.at_bats || 0);
        updateData.runs = (existing.runs || 0) + (stat.runs || 0);
        updateData.hits = (existing.hits || 0) + (stat.hits || 0);
        updateData.doubles = (existing.doubles || 0) + (stat.doubles || 0);
        updateData.triples = (existing.triples || 0) + (stat.triples || 0);
        updateData.home_runs = (existing.home_runs || 0) + (stat.home_runs || 0);
        updateData.rbi = (existing.rbi || 0) + (stat.rbi || 0);
        updateData.walks = (existing.walks || 0) + (stat.walks || 0);
        updateData.strikeouts = (existing.strikeouts || 0) + (stat.strikeouts || 0);
        updateData.stolen_bases = (existing.stolen_bases || 0) + (stat.stolen_bases || 0);
        if (updateData.at_bats > 0) {
          updateData.avg = (updateData.hits / updateData.at_bats).toFixed(3);
        }
      }
      
      const { error: updateError } = await supabase
        .from('player_season_stats')
        .update(updateData)
        .eq('player_id', stat.player_id);
      
      if (updateError) {
        console.error('❌ Season stat update error:', JSON.stringify(updateError));
      }
    } else {
      // INSERT: create new record
      const insertData: any = {
        player_id: stat.player_id,
        team_id: stat.team_id,
        league_id: stat.league_id,
        season_day: 0,
        season_year: stat.season_year || new Date().getFullYear(),
        games: 1,
        updated_at: new Date().toISOString(),
      };
      
      if (stat.is_pitcher) {
        Object.assign(insertData, {
          plate_appearances: 0, at_bats: 0, runs: 0, hits: 0, doubles: 0, triples: 0,
          home_runs: 0, rbi: 0, walks: 0, strikeouts: 0, stolen_bases: 0, avg: '0.000',
          wins: stat.win ? 1 : 0, losses: stat.loss ? 1 : 0,
          innings_pitched: stat.innings_pitched || 0, hits_allowed: stat.hits_allowed || 0,
          runs_allowed: stat.runs_allowed || 0, earned_runs: stat.earned_runs || 0,
          walks_allowed: stat.walks_allowed || 0, strikeouts_pitched: stat.strikeouts_pitched || 0,
          home_runs_allowed: stat.home_runs_allowed || 0, pitches_thrown: stat.pitches_thrown || 0,
          era: stat.innings_pitched > 0 ? ((stat.earned_runs * 9) / stat.innings_pitched).toFixed(2) : '0.00',
        });
      } else {
        Object.assign(insertData, {
          plate_appearances: stat.plate_appearances || 0, at_bats: stat.at_bats || 0,
          runs: stat.runs || 0, hits: stat.hits || 0, doubles: stat.doubles || 0,
          triples: stat.triples || 0, home_runs: stat.home_runs || 0, rbi: stat.rbi || 0,
          walks: stat.walks || 0, strikeouts: stat.strikeouts || 0, stolen_bases: stat.stolen_bases || 0,
          avg: stat.at_bats > 0 ? (stat.hits / stat.at_bats).toFixed(3) : '0.000',
          wins: 0, losses: 0, innings_pitched: 0, hits_allowed: 0, runs_allowed: 0,
          earned_runs: 0, walks_allowed: 0, strikeouts_pitched: 0, home_runs_allowed: 0,
          pitches_thrown: 0, era: '0.00',
        });
      }
      
      const { error: insertError } = await supabase
        .from('player_season_stats')
        .insert(insertData);
      
      if (insertError) {
        console.error('❌ Season stat insert error:', JSON.stringify(insertError));
      }
    }
  }
}

// Tiny daily sim-advancement tick — just updates current_day
// so the sim always has a "reason" to advance past empty-game days.
// NOT for visible growth — just keeps things moving.
async function processDailyAttributeTick(day: number): Promise<void> {
  if (!isSupabaseConfigured() || !supabase) return;
  
  console.log(`⏱️ Day ${day}: Daily sim-advancement tick`);
  
  try {
    await supabase!.from('simulation_state').upsert({
      id: 'global',
      current_day: day,
      updated_at: new Date().toISOString()
    });
  } catch (err) {
    console.log(`⚠️ Could not update sim state: ${err}`);
  }
}

// Real daily attribute growth — random roll each game day, modulated by age + hidden attrs
// Players don't grow linearly — some days they have breakthrough practice sessions, some days they don't
async function processDailyAttributeGrowth(day: number, playerIdsInGames: string[]): Promise<void> {
  if (!isSupabaseConfigured() || !supabase) return;
  if (playerIdsInGames.length === 0) return;
  
  console.log(`📈 Day ${day}: Attribute growth rolls for ${playerIdsInGames.length} players who played`);
  
  // Fetch all players who were in games today
  const { data: players } = await supabase
    .from('players')
    .select('id, age, is_hitter, consistency, intelligence, durability')
    .in('id', playerIdsInGames);
  
  if (!players || players.length === 0) return;
  
  // Growth mechanics:
  // 1. Age curve sets the BASE growth potential
  // 2. Consistency (hidden attr) = how reliable growth is (high consistency = steady growth, low = boom/bust)
  // 3. Intelligence (hidden attr) = slight bonus to all growth (smart players learn faster)
  // 4. Daily random roll — some days you train hard and get +0.2, other days you get +0.0
  //
  // Age base potential:
  //   18-23:  base +0.10  (young players learn faster)
  //   24-27:  base +0.06  (prime development years)
  //   28-29:  base +0.02  (nearing peak)
  //   30-32:  base -0.01  (early decline begins)
  //   33+:    base -0.04  (accelerating decline)
  
  // Consistency multiplier: affects the ROLL range
  //   Consistency 60+: roll 0.7x to 1.3x base (steady)
  //   Consistency 40-59: roll 0.3x to 1.7x base (variable)
  //   Consistency <40: roll 0.0x to 2.0x base (boom or bust)
  //
  // Intelligence: flat bonus to the final result (+0.0 to +0.02)
  // Durability: slightly reduces decline for aging players
  
  let totalGrowth = 0;
  let totalDecline = 0;
  
  for (const player of players) {
    const age = player.age || 25;
    const consistency = (player as any).consistency || 50;
    const intelligence = (player as any).intelligence || 50;
    const durability = (player as any).durability || 50;
    
    // 1. Age base potential
    let basePotential = 0;
    if (age <= 23) basePotential = 0.10;
    else if (age <= 27) basePotential = 0.06;
    else if (age <= 29) basePotential = 0.02;
    else if (age <= 32) basePotential = -0.01;
    else basePotential = -0.04;
    
    // 2. Daily roll: random factor modulated by consistency
    let rollMin: number, rollMax: number;
    if (consistency >= 60) {
      rollMin = 0.7;
      rollMax = 1.3;
    } else if (consistency >= 40) {
      rollMin = 0.3;
      rollMax = 1.7;
    } else {
      rollMin = 0.0;
      rollMax = 2.0;
    }
    
    const roll = rollMin + Math.random() * (rollMax - rollMin);
    
    // 3. Intelligence bonus (smart players squeeze more out of practice)
    const intBonus = (intelligence / 100) * 0.02; // 0.00 to 0.02
    
    // 4. Durability softens decline for aging players
    let durabilityMod = 1.0;
    if (basePotential < 0) {
      // Reduce decline by up to 50% for high durability
      durabilityMod = 1.0 - ((durability / 100) * 0.5);
    }
    
    // Final growth for this player TODAY
    let dailyGrowth = basePotential * roll + intBonus;
    if (dailyGrowth < 0) dailyGrowth *= durabilityMod;
    
    // Occasionally (5% chance) a player has a breakthrough day — double the growth
    if (Math.random() < 0.05) dailyGrowth *= 2;
    
    // Occasionally (3% chance) a player has a setback day — growth goes negative even if young
    if (Math.random() < 0.03 && dailyGrowth > 0) dailyGrowth *= -0.5;
    
    if (dailyGrowth > 0) totalGrowth += dailyGrowth;
    else totalDecline += Math.abs(dailyGrowth);
    
    const updates: Record<string, number> = {};
    
    if (player.is_hitter) {
      for (const attr of ['contact', 'power', 'discipline', 'speed', 'range', 'glove', 'arm']) {
        // Each attribute rolls slightly differently — some players improve contact but not power
        const attrVariance = 0.8 + Math.random() * 0.4; // 0.8x to 1.2x
        const clamped = Math.round((dailyGrowth * attrVariance) * 1000) / 1000;
        if (clamped !== 0) updates[attr] = clamped;
      }
    } else {
      for (const attr of ['velocity', 'movement', 'control', 'deception', 'hold_runner', 'range', 'glove']) {
        const attrVariance = 0.8 + Math.random() * 0.4;
        const clamped = Math.round((dailyGrowth * attrVariance) * 1000) / 1000;
        if (clamped !== 0) updates[attr] = clamped;
      }
    }
    
    // Hidden attrs grow slightly every day (experience compounds)
    const hiddenGrowth = Math.round((Math.abs(dailyGrowth) * 0.3 * (0.8 + Math.random() * 0.4)) * 1000) / 1000;
    updates.clutch = hiddenGrowth;
    updates.consistency = hiddenGrowth * 0.5; // Hard to change consistency
    updates.durability = hiddenGrowth * 0.3;  // Even harder to change durability
    updates.intelligence = Math.max(0.001, hiddenGrowth * 0.7); // Always positive
    
    if (Object.keys(updates).length === 0) continue;
    
    // Execute update via RPC or direct SQL
    await supabase.rpc('increment_player_attrs', {
      p_player_id: player.id,
      p_contact_val: updates.contact || 0,
      p_power_val: updates.power || 0,
      p_discipline_val: updates.discipline || 0,
      p_speed_val: updates.speed || 0,
      p_range_val: updates.range || 0,
      p_glove_val: updates.glove || 0,
      p_arm_val: updates.arm || 0,
      p_velocity_val: updates.velocity || 0,
      p_movement_val: updates.movement || 0,
      p_control_val: updates.control || 0,
      p_deception_val: updates.deception || 0,
      p_hold_runner_val: updates.hold_runner || 0,
      p_clutch_val: updates.clutch || 0,
      p_consistency_val: updates.consistency || 0,
      p_durability_val: updates.durability || 0,
      p_intelligence_val: updates.intelligence || 0
    });
    try {
      const directUpdates: Record<string, any> = {};
      for (const [key, val] of Object.entries(updates)) {
        if (val !== 0) directUpdates[key] = val;
      }
      if (Object.keys(directUpdates).length > 0) {
        directUpdates.updated_at = new Date().toISOString();
        await supabase!.from('players').update(directUpdates).eq('id', player.id);
      }
    } catch {}
  }
  
  if (Math.round(totalGrowth) > 0 || Math.round(totalDecline) > 0) {
    console.log(`📈 Day ${day}: +${Math.round(totalGrowth * 100) / 100} growth / -${Math.round(totalDecline * 100) / 100} decline today`);
  }
  
  // Update season_year_last_active for archiving
  let seasonYear = 1900;
  try {
    const { data: simState } = await supabase
      .from('simulation_state')
      .select('season_year')
      .eq('id', 'global')
      .single();
    if (simState?.season_year) seasonYear = simState.season_year;
  } catch (_) {}
  
  if (playerIdsInGames.length > 0) {
    await supabase
      .from('players')
      .update({ season_year_last_active: seasonYear })
      .in('id', playerIdsInGames);
  }
}

// Simulate all games for a day
// ALWAYS advances the sim — even on empty-game days via the tick
export async function simulateDay(day: number): Promise<{ gamesPlayed: number; results: any[]; ticked: boolean }> {
  const games = await getGamesForDay(day);
  const results: any[] = [];
  
  console.log(`📅 Simulating Day ${day}: ${games.length} games scheduled`);
  
  // Check for Day 7 roster fill
  if (day === 7) {
    console.log(`📋 Day 7: Checking and filling rosters...`);
    const { checkAndFillRosters } = await import('./SeasonManagerService');
    const rosterResult = await checkAndFillRosters();
    console.log(`✅ Roster fill complete: ${rosterResult.filled} players added to ${rosterResult.teamsAffected} teams`);
  }

  // Initialize stamina on Day 1
  if (day === 1) {
    const { initializePlayerStamina } = await import('./PlayerFatigueService');
    await initializePlayerStamina();
  }
  
  const playerIdsInGames: string[] = [];
  
  for (const game of games) {
    const result = await simulateAndSaveGame(game);
    results.push(result);
    // Collect all player IDs who played for attribute growth
    const { data: homePlayers } = await supabase!
      .from('player_game_stats')
      .select('player_id')
      .eq('game_id', game.id);
    if (homePlayers) homePlayers.forEach(p => playerIdsInGames.push(p.player_id));
  }

  // Process daily fatigue after all games are done
  if (games.length > 0) {
    const { processDailyFatigue } = await import('./PlayerFatigueService');
    const teamIds = new Set<string>();
    games.forEach(g => {
      teamIds.add(g.home_team_id);
      teamIds.add(g.away_team_id);
    });
    await processDailyFatigue(day, Array.from(teamIds));
  }
  
  // ALWAYS tick: advance sim state, even on off-days
  await processDailyAttributeTick(day);
  
  // Real attribute growth for players who actually played
  await processDailyAttributeGrowth(day, playerIdsInGames);
  
  return { gamesPlayed: games.length, results, ticked: true };
}

// Simulate one league's games for a day (per-league ticking)
// ALWAYS advances the sim via attribute tick, even on 0-game days
export async function simulateLeagueDay(leagueId: string, day: number): Promise<{ gamesPlayed: number; results: any[] }> {
  const games = await getLeagueGamesForDay(leagueId, day);
  const results: any[] = [];
  
  console.log(`📅 [${leagueId}] Simulating Day ${day}: ${games.length} games scheduled`);
  
  for (const game of games) {
    const result = await simulateAndSaveGame(game);
    results.push(result);
  }

  // Process daily fatigue for teams in this league's games
  const { processDailyFatigue } = await import('./PlayerFatigueService');
  const teamIds = new Set<string>();
  games.forEach(g => {
    teamIds.add(g.home_team_id);
    teamIds.add(g.away_team_id);
  });
  await processDailyFatigue(day, Array.from(teamIds));
  
  return { gamesPlayed: games.length, results };
}

// Check if regular season is complete
export async function isRegularSeasonComplete(leagueId: string): Promise<boolean> {
  if (!isSupabaseConfigured() || !supabase) return false;
  
  const { data: schedule } = await supabase
    .from('league_schedule')
    .select('status')
    .eq('league_id', leagueId)
    .limit(5000);
  
  if (!schedule || schedule.length === 0) return false;
  
  const completed = schedule.filter((g: any) => g.status === 'completed').length;
  const total = schedule.length;
  
  console.log(`📊 League ${leagueId}: ${completed}/${total} games completed`);
  
  // Season complete when all games are done
  return completed >= total * 0.99; // Allow for some rounding
}

// Get current day from simulation state
export async function getCurrentSimulationDay(): Promise<number> {
  if (!isSupabaseConfigured() || !supabase) return 0;
  
  const { data } = await supabase
    .from('simulation_state')
    .select('current_day')
    .eq('id', 'global')
    .single();
  
  return data?.current_day || 0;
}

// Get playoff status
export async function getPlayoffStatus(leagueId: string): Promise<{ inProgress: boolean; bracketId: string | null }> {
  if (!isSupabaseConfigured() || !supabase) return { inProgress: false, bracketId: null };
  
  const { data } = await supabase
    .from('playoff_brackets')
    .select('id, status')
    .eq('league_id', leagueId)
    .single();
  
  return {
    inProgress: data?.status === 'in_progress',
    bracketId: data?.id || null
  };
}

// Start playoffs for a league
export async function startPlayoffs(leagueId: string): Promise<void> {
  if (!isSupabaseConfigured() || !supabase) return;
  
  // Get standings - top 2 from each division
  const { data: standings } = await supabase
    .from('league_standings')
    .select('*')
    .eq('league_id', leagueId)
    .order('wins', { ascending: false });
  
  if (!standings || standings.length < 8) {
    console.warn('Not enough teams for playoffs');
    return;
  }
  
  // Update bracket status
  await supabase.from('playoff_brackets').update({
    status: 'in_progress'
  }).eq('league_id', leagueId);
  
  console.log(`🏆 Playoffs started for league ${leagueId}`);
}

// Instant simulation - simulate all remaining games (up to day 186)
// Uses attribute tick to advance through empty-game days
interface SimState { day: number; completed: boolean }
const dayState: Map<number, SimState> = new Map();

export async function instantSimulate(): Promise<{ daysSimulated: number; gamesPlayed: number }> {
  let currentDay = await getCurrentSimulationDay();
  let daysSimulated = 0;
  let gamesPlayed = 0;
  const MAX_DAY = 186; // Full regular season + playoffs
  
  console.log(`⚡ Starting instant simulation from Day ${currentDay}...`);
  
  // Simulate up to MAX_DAY days (covers regular season + playoffs)
  while (currentDay < MAX_DAY) {
    const result = await simulateDay(currentDay);
    
    // Day 7: Fill rosters if needed
    if (currentDay === 6) { // Before advancing to day 7
      const { checkAndFillRosters } = await import('./SeasonManagerService');
      const rosterResult = await checkAndFillRosters();
      console.log(`📋 Day 7 roster fill: ${rosterResult.filled} players added to ${rosterResult.teamsAffected} teams`);
    }
    
    // Don't break on 0 games anymore — attribute tick advances the day
    gamesPlayed += result.gamesPlayed;
    daysSimulated++;
    currentDay++;
    
    // Update simulation state periodically
    if (daysSimulated % 10 === 0) {
      if (isSupabaseConfigured() && supabase) {
        await supabase.from('simulation_state').upsert({
          id: 'global',
          current_day: currentDay,
          is_running: true,
          updated_at: new Date().toISOString()
        });
      }
    }
  }
  
  // Final update
  if (isSupabaseConfigured() && supabase) {
    await supabase.from('simulation_state').upsert({
      id: 'global',
      current_day: currentDay,
      is_running: false,
      updated_at: new Date().toISOString()
    });
  }
  
  console.log(`✅ Instant simulation complete: ${daysSimulated} days, ${gamesPlayed} games`);
  
  return { daysSimulated, gamesPlayed };
}

// Simulate until playoffs start (Day 161)
// Uses attribute tick to advance through empty-game days
export async function simulateToPlayoffs(): Promise<{ daysSimulated: number; gamesPlayed: number }> {
  let currentDay = await getCurrentSimulationDay();
  let daysSimulated = 0;
  let gamesPlayed = 0;
  
  console.log(`🏃 Simulating to playoffs from Day ${currentDay}...`);
  
  while (currentDay < 161) {
    const result = await simulateDay(currentDay);
    
    // Day 7: Fill rosters if needed
    if (currentDay === 6) { // Before advancing to day 7
      const { checkAndFillRosters } = await import('./SeasonManagerService');
      const rosterResult = await checkAndFillRosters();
      console.log(`📋 Day 7 roster fill: ${rosterResult.filled} players added to ${rosterResult.teamsAffected} teams`);
    }
    
    // Don't break on 0 games anymore — attribute tick advances the day
    gamesPlayed += result.gamesPlayed;
    daysSimulated++;
    currentDay++;
    
    // Update state
    if (isSupabaseConfigured() && supabase) {
      await supabase.from('simulation_state').upsert({
        id: 'global',
        current_day: currentDay,
        is_running: true,
        updated_at: new Date().toISOString()
      });
    }
  }
  
  console.log(`✅ Simulated to playoffs: ${daysSimulated} days, ${gamesPlayed} games`);
  
  return { daysSimulated, gamesPlayed };
}
